import React from 'react';
import PropTypes from 'prop-types';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import EqualsSignIcon from 'components/pure/utils/icon/effectiveness/EqualsSignIcon';
import ThumbDownIcon from 'components/pure/utils/icon/effectiveness/ThumbDownIcon';
import ThumbUpIcon from 'components/pure/utils/icon/effectiveness/ThumbUpIcon';
import User from 'records/User';
import './CoachingEffectivenessScore.styl';
import round from 'utils/round';
import moment from 'moment';
import ExecDashboardSource from 'sources/ExecDashboardSource';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import convertDateToUTC from 'utils/datetime/convertToMidnightUTC';
import {formatDuration} from 'components/dashboard/coaching-effectiveness/common';
import theme from 'styles/theme';
import {addSubtractDayIntervalEndDateExclusive} from '@store/coaching-plans';
import {LineProgressCombo} from 'charts';
import {TOOLTIP_DELAY} from 'components/StatsPage2.utils';
import {getMuiV4Theme} from 'utils/css/themeUtils';

export const NEUTRAL_MIN = -5;
export const NEUTRAL_MAX = 5;

function goodScore(score) {
  return score >= NEUTRAL_MAX;
}

function badScore(score) {
  return score <= NEUTRAL_MIN;
}

const goodColor = theme.palette.pastelGood;
const badColor = theme.palette.pastelBad;
const neutralColor = theme.palette.pastelNeutral;

/**
 * get icon/color combo for score
 * @param score
 * @return {*}
 */
function stylesForScore(score) {
  if (goodScore(score)) {
    return [goodColor, <ThumbUpIcon />, <ThumbUpIcon inverted />];
  }
  if (badScore(score)) {
    return [badColor, <ThumbDownIcon />, <ThumbDownIcon inverted />];
  }
  return [neutralColor, <EqualsSignIcon />, <EqualsSignIcon inverted />];
}

/**
 * Render the coaching effectiveness cell, which is mainly just the icon
 * until the tooltip pops up - this nests the tooltip as well
 */
export default class CoachingEffectivenessScore extends React.PureComponent {
  static propTypes = {
    // effectiveness score, in the range [-100, 100]
    score: PropTypes.number,
    // User
    manager: PropTypes.instanceOf(User),
    from: PropTypes.object,
    to: PropTypes.object,
    currentPeriodActivityTrend: PropTypes.number,
    currentPeriodScorecardTrend: PropTypes.number,
  };

  constructor(props) {
    super(props);

    this.state = {
      loadingPriorPeriod: false,
      priorPeriodCoachingTime: undefined,
    };
  }

  loadPriorPeriodCoaching(managerId, startDate, endDate) {
    this.setState({loadingPriorPeriod: true}, () => {
      ExecDashboardSource.getCoachingTimeTrend({
        managerId,
        startDate,
        endDate,
      }).then(result => {
        this.setState({
          loadingPriorPeriod: false,
          priorPeriodCoachingTime: result,
        });
      });
    });
  }

  render() {
    const {
      score,
      manager,
      startDate,
      endDate,
      currentPeriodActivityTrend,
      currentPeriodScorecardTrend,
    } = this.props;
    const {loadingPriorPeriod, priorPeriodCoachingTime} = this.state;

    const [color, icon, tooltipIcon] = stylesForScore(score);

    const {startDate: previousStartDate, endDate: previousEndDate} =
      addSubtractDayIntervalEndDateExclusive('subtract', startDate, endDate);

    return (
      <div className="effectiveness-score-wrapper">
        <GenericTooltip
          tooltip={
            <CoachingTimeComboChartTooltip
              priorPeriodStart={previousStartDate.toDate()}
              priorPeriodEnd={previousEndDate.toDate()}
              from={startDate.toDate()}
              // we're end date exclusive in how we treat this guy,
              // but when displaying, the user doesnt know that. roll it back a day
              to={endDate.clone().subtract(1, 'days').toDate()}
              score={score}
              manager={manager}
              color={color}
              icon={tooltipIcon}
              scorecardBar={currentPeriodScorecardTrend}
              activityBar={currentPeriodActivityTrend}
              loadingPriorPeriod={loadingPriorPeriod}
              priorPeriodCoachingTime={priorPeriodCoachingTime}
            />
          }
          positionTop
          delay={TOOLTIP_DELAY}
          className="effectiveness-score-popover"
          onOpen={() =>
            this.loadPriorPeriodCoaching(manager.id, previousStartDate, previousEndDate)
          }>
          {icon}
        </GenericTooltip>
      </div>
    );
  }
}

/**
 * A little helper to compartmentalize some of the tooltip info and display that.
 * Tooltip comes from ev-charts
 */
const CoachingTimeComboChartTooltip = ({
  priorPeriodStart,
  priorPeriodEnd,
  from,
  to,
  score,
  manager,
  color,
  icon,
  scorecardBar: scorecardProgress,
  activityBar: activityProgress,
  loadingPriorPeriod,
  priorPeriodCoachingTime,
}) => {
  const roundedScore = round(score, 2);

  const hoursMeetingTime = {
    color: theme.palette.dustyGray,
    legendLabel: 'hours meeting time',
    data: priorPeriodCoachingTime
      ? priorPeriodCoachingTime.meetingActivity.data.map(i => ({
          date: convertDateToUTC(new Date(i.date)),
          value: i.value ?? 0,
        }))
      : [],
  };

  const hoursReviewTime = {
    color: getMuiV4Theme().palette.primary.main,
    legendLabel: 'hours review time',
    data: priorPeriodCoachingTime
      ? priorPeriodCoachingTime.reviewActivity.data.map(i => ({
          date: convertDateToUTC(new Date(i.date)),
          value: i.value ?? 0,
        }))
      : [],
  };

  return (
    <div>
      <div className="banner" style={{backgroundColor: color}}>
        {icon}{' '}
        <h3>
          {manager.firstName}'s Coaching Effectiveness Score: {roundedScore}%
        </h3>
      </div>

      <section className="combo-chart-wrapper">
        {loadingPriorPeriod && <LoadingOverlay />}
        <LineProgressCombo
          periods={[
            {
              from: priorPeriodStart,
              to: priorPeriodEnd,
            },
            {from: from, to: to},
          ]}
          series={[hoursMeetingTime, hoursReviewTime]}
          progress={[
            {
              highlight: scorecardProgress > 0 ? goodColor : badColor,
              background: theme.palette.alto,
              progress: scorecardProgress,
              label: 'Scores',
            },
            {
              highlight: activityProgress > 0 ? goodColor : badColor,
              background: theme.palette.alto,
              progress: activityProgress,
              label: 'Call Activity',
            },
          ]}
          firstNamePossessive={manager.firstName + "'s"}
          formatValue={value => formatDuration(moment.duration(value, 'seconds'))}
          background={false}
        />
      </section>
    </div>
  );
};
